import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { Plus, Send, BarChart2, Loader2, Pencil, Trash2, RefreshCw } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { WA_CAMPAIGNS_URL, WA_CAMPAIGN_START_URL } from "../../constants/api";

const STATUS_COLORS = {
    draft:      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    scheduled:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    running:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    paused:     'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:  'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    cancelled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    failed:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const pct = (num, den) => den > 0 ? `${Math.round(num / den * 100)}%` : '—';

/* Nothing has gone out yet, so the campaign is still the sender's to change.
   'failed' belongs here: a campaign that could not build a recipient list never
   sent anything, and its whole point is to be fixed and retried. */
const NOT_YET_RUN = ['draft', 'scheduled', 'failed'];

export default function WhatsAppCampaignList() {
    const navigate = useNavigate();
    const { fetch, create, remove } = useApiMethods();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        const res = await fetch(WA_CAMPAIGNS_URL);
        if (res?.status === 'success') setCampaigns(res.data || []);
        setLoading(false);
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Send now, ahead of a schedule. Confirmed first: for a scheduled campaign
       this overrides the time that was picked, and there is no undo once the
       messages are with the provider. */
    const startNow = async (c) => {
        const when = c.schedule_at
            ? `It is scheduled for ${new Date(c.schedule_at).toLocaleString()}. Send "${c.name}" now instead?`
            : `Send "${c.name}" now? This messages the whole audience.`;
        if (!window.confirm(when)) return;
        setBusyId(c.id);
        const res = await create(WA_CAMPAIGN_START_URL(c.id), {});
        setBusyId(null);
        if (res?.status === 'success') {
            ToastMsg('success', res.message || 'Campaign started');
            load();
        } else {
            ToastMsg('error', res?.message || 'Could not start campaign');
        }
    };

    const destroy = async (c) => {
        if (!window.confirm(`Delete "${c.name}"? ${c.status === 'scheduled'
            ? 'It will not go out at its scheduled time.' : ''}`)) return;
        setBusyId(c.id);
        const res = await remove(`${WA_CAMPAIGNS_URL}?id=${c.id}`);
        setBusyId(null);
        if (res?.status === 'success') {
            ToastMsg('success', 'Campaign deleted');
            load();
        } else {
            ToastMsg('error', res?.message || 'Could not delete campaign');
        }
    };

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">WhatsApp Campaigns</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Bulk template message campaigns</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* A scheduled campaign turns itself into a running one, so the
                            table goes stale on its own. */}
                        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                                className="flex items-center gap-1 text-xs">
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                        <Button onClick={() => navigate('/whatsapp/campaigns/new')} className="flex items-center gap-2 text-sm">
                            <Plus className="h-4 w-4" /> New Campaign
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-2 font-medium">Name</th>
                                    <th className="pb-2 font-medium">Template</th>
                                    <th className="pb-2 font-medium">Status</th>
                                    <th className="pb-2 font-medium">Recipients</th>
                                    <th className="pb-2 font-medium">Sent</th>
                                    <th className="pb-2 font-medium">Delivered</th>
                                    <th className="pb-2 font-medium">Read</th>
                                    <th className="pb-2 font-medium">Failed</th>
                                    <th className="pb-2 font-medium">Scheduled</th>
                                    <th className="pb-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map(c => (
                                    <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="py-3 font-medium text-gray-800 dark:text-gray-100">{c.name}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400 text-xs">{c.template_name}</td>
                                        <td className="py-3">
                                            <span className={cn(
                                                'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full',
                                                STATUS_COLORS[c.status] || STATUS_COLORS.draft
                                            )}>
                                                {c.status === 'running' && (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                                                )}
                                                {c.status}
                                            </span>
                                        </td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.total_recipients}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.sent_count}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.delivered_count} <span className="text-xs text-gray-400">({pct(c.delivered_count, c.sent_count)})</span></td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.read_count} <span className="text-xs text-gray-400">({pct(c.read_count, c.sent_count)})</span></td>
                                        <td className="py-3 text-red-500">{c.failed_count}</td>
                                        <td className="py-3 text-gray-500 text-xs">
                                            {c.schedule_at ? new Date(c.schedule_at).toLocaleString() : '—'}
                                            {c.status === 'scheduled' && c.schedule_at && (
                                                <span className="block text-[10px] text-blue-500">
                                                    {new Date(c.schedule_at) > new Date() ? 'waiting' : 'due — starting shortly'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/whatsapp/campaigns/${c.id}/report`)}
                                                    className="text-gray-400 hover:text-blue-500"
                                                    title="View Report"
                                                >
                                                    <BarChart2 className="h-4 w-4" />
                                                </button>
                                                {NOT_YET_RUN.includes(c.status) && (
                                                    <>
                                                        <button
                                                            onClick={() => navigate(`/whatsapp/campaigns/${c.id}/edit`)}
                                                            className="text-gray-400 hover:text-blue-500"
                                                            title={c.status === 'scheduled'
                                                                ? 'Edit — including the scheduled time'
                                                                : 'Edit campaign'}
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => startNow(c)}
                                                            disabled={busyId === c.id}
                                                            className="text-gray-400 hover:text-green-500 disabled:opacity-40"
                                                            title={c.status === 'scheduled'
                                                                ? 'Send now instead of waiting'
                                                                : 'Send now'}
                                                        >
                                                            {busyId === c.id
                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                : <Send className="h-4 w-4" />}
                                                        </button>
                                                        <button
                                                            onClick={() => destroy(c)}
                                                            disabled={busyId === c.id}
                                                            className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                                                            title="Delete campaign"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {campaigns.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="py-8 text-center text-sm text-gray-400">
                                            No campaigns yet. Create your first bulk WhatsApp campaign.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </ContainerCard>
        </div>
    );
}
